import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

// =====================================================
// STATIC FAQ DATA
// =====================================================

const faqs = [
  {
    question: "What are API Services?",
    answer:
      "API services allow your website, mobile application, or backend system to communicate with external communication infrastructure programmatically. They make it possible to trigger messaging, authentication, notifications, and other workflows directly from your application.",
  },
  {
    question: "What API services do you provide?",
    answer:
      "Our API services currently focus on business communication and authentication, including WhatsApp API Services and SMS OTP Services. These can be integrated into existing applications to automate customer communication and verification workflows.",
  },
  {
    question: "How can I integrate your APIs into my application?",
    answer:
      "Your development team can integrate the APIs using standard HTTP requests. The integration involves authentication, sending the required parameters to the appropriate endpoint, handling the API response, and incorporating the result into your application's workflow.",
  },
  {
    question: "What can I use the SMS OTP API for?",
    answer:
      "SMS OTP APIs can be used for user registration, login verification, mobile number verification, password recovery, transaction authentication, and other workflows where phone-based verification is required.",
  },
  {
    question: "Can WhatsApp and SMS APIs be integrated into an existing system?",
    answer:
      "Yes. The APIs can be integrated with existing websites, mobile applications, backend systems, customer portals, and business platforms, allowing your current application to trigger communication without rebuilding the entire system.",
  },
  {
    question: "Is API integration suitable for growing businesses?",
    answer:
      "Yes. API-based communication allows businesses to automate repetitive communication workflows and connect those workflows directly with their existing applications. This makes it easier to expand communication capabilities as business requirements grow.",
  },
  {
    question: "How do I get started with the API services?",
    answer:
      "Start by discussing your application requirements with our team. We can help identify the appropriate API service, integration approach, and communication workflow for your business.",
  },
];

// =====================================================
// FAQ SECTION
// =====================================================

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section
      id="faq"
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-6
        sm:py-8
        md:py-10
        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
          Brand color remains unchanged in both themes.
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          top-20
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-80
          sm:w-80
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-40
          bottom-10
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-80
          sm:w-80
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-8
            max-w-3xl
            text-center
            sm:mb-10
            lg:mb-12
          "
        >
          {/* Label */}

          <motion.span
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
            }}
            className="
              inline-flex
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              text-[var(--brand-light)]
              sm:px-4
              sm:py-2
              sm:text-xs
            "
          >
            FAQs
          </motion.span>

          {/* Heading */}

          <h2
            className="
              mt-4
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              text-[var(--text-primary)]
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          >
            Frequently Asked{" "}
            <span className="text-[var(--brand-primary)]">
              Questions
            </span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-5xl
              text-xs
              leading-5
              text-[var(--text-muted)]
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
          >
            Get quick answers to common questions about our API
            services, integration, authentication, communication
            workflows, and implementation.
          </p>
        </motion.div>

        {/* =====================================================
            FAQ GRID
        ====================================================== */}

        <div
          className="
            mx-auto
            grid
            w-full
            max-w-6xl
            grid-cols-1
            gap-3
            sm:gap-4
          "
        >
          {faqs.map((faq, index) => {
            const isOpen = activeIndex === index;

            return (
              <motion.div
                key={faq.question}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.08,
                  ease: "easeOut",
                }}
                className="
                  group
                  overflow-hidden
                  rounded-xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg)]
                  transition-all
                  duration-300
                  hover:border-orange-500/30
                  sm:rounded-2xl
                "
              >
                {/* =================================================
                    QUESTION
                ================================================== */}

                <button
                  type="button"
                  onClick={() =>
                    setActiveIndex(isOpen ? -1 : index)
                  }
                  aria-expanded={isOpen}
                  className="
                    flex
                    w-full
                    items-center
                    justify-between
                    gap-4
                    px-4
                    py-4
                    text-left
                    transition-all
                    duration-300
                    hover:bg-[var(--bg-tertiary)]
                    sm:px-5
                    sm:py-5
                    md:px-6
                  "
                >
                  <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                    {/* Number */}

                    <span
                      className="
                        shrink-0
                        text-[10px]
                        font-semibold
                        tracking-[1.5px]
                        text-[var(--brand-primary)]
                        opacity-70
                        sm:text-xs
                      "
                    >
                      0{index + 1}
                    </span>

                    {/* Question */}

                    <h3
                      className="
                        text-sm
                        font-semibold
                        leading-5
                        text-[var(--text-primary)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--brand-light)]
                        sm:text-base
                        sm:leading-6
                        md:text-lg
                      "
                    >
                      {faq.question}
                    </h3>
                  </div>

                  {/* Plus / Minus */}

                  <div
                    className={`
                      flex
                      h-8
                      w-8
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      sm:h-9
                      sm:w-9
                      ${
                        isOpen
                          ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                          : "border-orange-500/20 bg-orange-500/[0.08] text-[var(--brand-primary)]"
                      }
                    `}
                  >
                    {isOpen ? (
                      <Minus size={16} />
                    ) : (
                      <Plus size={16} />
                    )}
                  </div>
                </button>

                {/* =================================================
                    ANSWER
                ================================================== */}

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                    >
                      <div
                        className="
                          border-t
                          border-[var(--border-primary)]
                          px-4
                          pb-4
                          pt-3
                          sm:px-5
                          sm:pb-5
                          sm:pt-4
                          md:px-6
                        "
                      >
                        <p
                          className="
                            pl-7
                            text-xs
                            leading-6
                            text-[var(--text-muted)]
                            sm:pl-9
                            sm:text-sm
                            sm:leading-6
                          "
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;